import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { 
  faChevronLeft, 
  faFileAlt, 
  faCheck,
  faDownload,
  faCopy,
  faSpinner,
  faTimes,
  faPaperPlane,
  faCircleCheck
} from '@fortawesome/free-solid-svg-icons';
import { IDocumentFilesResponse, IDocumentFileResponse } from 'src/app/models/document.model';
import { DocumentsService } from 'src/app/services/documents.service';
import { DownloadService } from 'src/app/services/download.service';
import { AuthService } from 'src/app/services/auth.service';
import { Store } from '@ngrx/store';
import { setWarningText } from 'src/app/reducers/warning-popup.reducer';

@Component({
  selector: 'app-document-success-v2',
  templateUrl: './document-success.component.html',
  styleUrls: ['./document-success.component.sass']
})
export class DocumentSuccessV2Component implements OnInit {
  // Icons
  faChevronLeft = faChevronLeft;
  faFileAlt = faFileAlt;
  faCheck = faCheck;
  faDownload = faDownload;
  faCopy = faCopy;
  faSpinner = faSpinner;
  faTimes = faTimes;
  faPaperPlane = faPaperPlane;
  faCircleCheck = faCircleCheck;

  // State
  loading = true;
  showConfirmSendModal = false;

  // Data from query params
  generatedDocsResponse: IDocumentFilesResponse | null = null;
  patientId = '';
  treatmentPlanId: string | null = null;

  // Download/Send state
  filesToSign: IDocumentFileResponse[] = [];
  sentStatus = 'NOT_SENT';
  btnLoadings: string[] = [];
  disableAllButtons = false;
  sendDocType = 'DELIVERY_METHOD_SMS';

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private documentsService: DocumentsService,
    private downloadService: DownloadService,
    private authService: AuthService,
    private store: Store
  ) {
    this.sendDocType = this.authService.getSendDocsType();
  }

  ngOnInit(): void {
    this.loadStateFromParams();
  }

  loadStateFromParams(): void {
    const stateParam = this.route.snapshot.queryParams['state'];
    this.patientId = this.route.snapshot.queryParams['patient_id'] || '';

    if (stateParam) {
      try {
        this.generatedDocsResponse = JSON.parse(atob(stateParam)) as IDocumentFilesResponse;
        this.treatmentPlanId = this.generatedDocsResponse.treatment_plan_id || null;
        
        // Verificar documentos já enviados
        if (this.generatedDocsResponse.answer_group_id) {
          const documentsIds = this.generatedDocsResponse.documents_link_info.map(doc => doc.document_id);
          this.getAlreadySentDocuments(documentsIds);
        }
      } catch (e) {
        console.error('Error parsing state:', e);
        this.router.navigate(['main', 'v2', 'patients']);
      }
    } else {
      this.router.navigate(['main', 'v2', 'patients']);
    }

    this.loading = false;
  }

  getAlreadySentDocuments(documentsIds: string[]): void {
    if (!this.generatedDocsResponse?.answer_group_id) return;

    this.documentsService.getAlreadySentDocuments(this.generatedDocsResponse.answer_group_id, documentsIds)
      .then((response) => {
        // Usar final_status da resposta para definir o estado do botão
        if (response.final_status && response.final_status !== 'NOT_SENT') {
          this.sentStatus = response.final_status;
        }

        if (response.documents && response.documents.length > 0) {
          this.filesToSign = response.documents;
          
          // Verificar se há documentos enviados como link
          const sentAsLinkDocs = response.documents.filter(doc => doc.status === 'SENT_AS_LINK');
          if (sentAsLinkDocs.length > 0) {
            this.sentStatus = 'SENT';
          }
        }

        // Atualiza o plano de tratamento se existir na resposta
        if (response.treatment_plan_id) {
          this.treatmentPlanId = response.treatment_plan_id;
        }
      })
      .catch((error: unknown) => {
        console.error('Error getting already sent documents:', error);
      });
  }

  goBack(): void {
    if (this.patientId) {
      this.router.navigate(['main', 'v2', 'patients', this.patientId, 'edit']);
    } else {
      this.router.navigate(['main', 'v2', 'patients']);
    }
  }

  // ========== DOWNLOAD METHODS ==========

  onClickDownloadFile(index: number, link: string, fileName: string, type: 'pdf' | 'docx'): void {
    if (!link) return;
    
    const loadingKey = `${index}-${type}`;
    this.btnLoadings.push(loadingKey);

    this.downloadService.downloadFile(link, `${fileName}`, type)
      .then(() => {
        this.btnLoadings = this.btnLoadings.filter(k => k !== loadingKey);
      })
      .catch((error: unknown) => {
        console.error('Error downloading file:', error);
        this.btnLoadings = this.btnLoadings.filter(k => k !== loadingKey);
      });
  }

  isDownloading(index: number, type: string): boolean {
    return this.btnLoadings.includes(`${index}-${type}`);
  }

  // ========== SEND METHODS ==========

  sendAllDocuments(): void {
    this.showConfirmSendModal = true;
  }

  closeConfirmSendModal(): void {
    this.showConfirmSendModal = false;
  }

  getDocumentsToSend(): IDocumentFileResponse[] {
    if (!this.generatedDocsResponse) return [];
    return this.generatedDocsResponse.documents_link_info
      .filter(doc => !this.filesToSign.some(f => f.document_id === doc.document_id));
  }

  confirmSendDocuments(): void {
    if (!this.generatedDocsResponse) return;
    
    this.showConfirmSendModal = false;
    this.sentStatus = 'SENDING';
    
    const documentsToSend = this.getDocumentsToSend();

    if (documentsToSend.length === 0) {
      this.sentStatus = 'SENT';
      return;
    }

    const documentIdList = documentsToSend.map(d => d.document_id);
    
    this.documentsService.sendDocumentToSign(documentIdList, this.generatedDocsResponse, this.patientId)
      .then((response) => {
        this.sentStatus = 'SENT';
        this.filesToSign = [...this.filesToSign, ...documentsToSend];
        
        // Captura o treatment_plan_id se retornado
        if (response?.treatment_plan_id) {
          this.treatmentPlanId = response.treatment_plan_id;
        }
      })
      .catch((error: any) => {
        console.error('Error sending documents:', error);
        this.sentStatus = 'NOT_SENT';
        const errorMessage = error?.error?.message || error?.message || 'Erro ao enviar documentos. Tente novamente.';
        this.store.dispatch(setWarningText(errorMessage));
      });
  }

  viewTreatmentPlan(): void {
    if (this.treatmentPlanId) {
      this.router.navigate(['main', 'v2', 'treatment-plan', this.treatmentPlanId]);
    }
  }

  getSendButtonText(): string {
    const texts: { [key: string]: string } = {
      'NOT_SENT': 'Enviar todos os documentos',
      'SENDING': 'Enviando...',
      'SENT': 'Documentos enviados!'
    };
    return texts[this.sentStatus] || 'Enviar todos os documentos';
  }

  canSendDocuments(): boolean {
    return this.sentStatus === 'NOT_SENT' && 
           this.generatedDocsResponse !== null && 
           this.generatedDocsResponse.documents_link_info.length > 0 &&
           this.getDocumentsToSend().length > 0;
  }

  isDocumentSent(doc: IDocumentFileResponse): boolean {
    return this.filesToSign.some(f => f.document_id === doc.document_id);
  }
}
